import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const values = [
  { icon: "ri-empathize-line", title: "尊重习惯", desc: "不强迫任何人改变阅读方式，把选择权交给读者，怎么舒服怎么来。" },
  { icon: "ri-leaf-line", title: "护眼优先", desc: "从夜间模式到亮度自适应，始终把读者眼睛的舒适放在第一位。" },
  { icon: "ri-shield-check-line", title: "随时可看", desc: "离线缓存让阅读不依赖网络，精彩内容随时都在手边。" },
  { icon: "ri-rocket-line", title: "顺滑体验", desc: "翻页、下拉、横屏皆顺滑流畅，专注内容本身，不被卡顿打扰。" },
];

export default function About() {
  useSeo(
    "关于扑飞漫画网 - 品牌介绍与内容原则",
    "了解扑飞漫画网的品牌定位、发展理念与内容原则，我们致力于打造翻页顺滑、夜间护眼、缓存可看的多阅读姿势漫画平台。"
  );

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于扑飞漫画网",
    description: "扑飞漫画网的品牌介绍、发展理念、内容原则与联系方式。",
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
        <section className="relative overflow-hidden bg-secondary-800">
          <div className="absolute -right-10 -top-12 h-56 w-56 rounded-full bg-primary-500/30 blur-3xl" />
          <div className="absolute -bottom-14 -left-8 h-48 w-48 rounded-full bg-accent-500/25 blur-3xl" />
          <div className="relative mx-auto w-full max-w-7xl px-4 py-16 md:px-8 md:py-24">
            <nav aria-label="面包屑" className="mb-5 text-xs text-background-200/80">
              <Link to="/" className="hover:text-primary-400">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-background-100">关于我们</span>
            </nav>
            <h1 className="max-w-2xl text-3xl leading-snug text-background-50 md:text-5xl">
              让每一页，都合你心意
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              扑飞漫画网是一个专注阅读体验的漫画平台。我们相信，好的阅读不该被单一模式束缚——翻页、下拉、目录跳转、夜间模式、缓存、横屏，总有一种方式适合当下的你。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="text-sm font-semibold tracking-wide text-primary-500">关于我们</span>
              <h2 className="mt-2 text-2xl leading-snug md:text-4xl">我们是谁，我们在做什么</h2>
              <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">
                扑飞漫画网由一群热爱漫画、也热爱打磨细节的人创建。我们知道，深夜追更时屏幕太刺眼有多扫兴，也明白地铁里没信号时错过更新有多遗憾。
              </p>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                因此我们从"怎么读得舒服"出发，把翻页、下拉、夜间、缓存、横屏这些能力做顺做扎实。我们做内容，但更用心做好"读"这个体验本身。
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {values.map((v) => (
                <div key={v.title} className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
                    <i className={v.icon} />
                  </span>
                  <h3 className="mt-4 font-heading text-base text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100/70 py-12 md:py-16">
          <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
            <div className="text-center">
              <span className="text-sm font-semibold tracking-wide text-primary-500">内容原则</span>
              <h2 className="mt-2 text-2xl md:text-4xl">我们坚守的三条原则</h2>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-3">
              {[
                { n: "01", t: "内容择优", d: "精选口碑与人气俱佳的作品，持续更新，确保读者每次打开都有值得追的精彩内容。" },
                { n: "02", t: "体验至上", d: "阅读速度、夜间护眼、离线可用，每一个环节都为更顺滑的阅读体验而优化。" },
                { n: "03", t: "尊重读者", d: "不做强制引导，不捆绑单一模式，把阅读的主动权完整交还给读者。" },
              ].map((i) => (
                <div key={i.n} className="rounded-lg border border-background-200/70 bg-background-50 p-6">
                  <span className="font-heading text-4xl text-primary-300">{i.n}</span>
                  <h3 className="mt-4 font-heading text-lg text-foreground-950">{i.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{i.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <div className="rounded-xl border border-background-200/70 bg-background-100 p-8 md:p-12">
            <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
              <div>
                <h2 className="text-2xl leading-snug md:text-3xl">想合作或反馈建议？</h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                  无论是内容合作、功能建议还是问题反馈，欢迎随时联系我们，我们期待听到你的声音。
                </p>
                <div className="mt-6 space-y-3 text-sm text-foreground-700">
                  <p className="flex items-center gap-3">
                    <i className="ri-mail-line text-primary-500" /> contact@pufeimanga.example
                  </p>
                  <p className="flex items-center gap-3">
                    <i className="ri-time-line text-primary-500" /> 工作日 9:00 - 18:00
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  填写联系表单 <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/comics"
                  className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-6 py-3 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
                >
                  去逛逛漫画库
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}